import { cloneElement, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import Mousetrap from 'mousetrap';

import { CloseBoldIcon, RepromptIcon, SearchIcon } from '../../icons';
import Input from '../Input';

import styles from './_index.module.scss';

export interface SearchProps {
  className?: string;
  hasAutoFocus?: boolean;
  icon?: React.ReactElement;
  isClearable?: boolean;
  isIconVisible?: boolean;
  isScrollable?: boolean;
  isSearching?: boolean;
  onChange: (value: number | string) => void;
  placeholder?: string;
  results?: Array<{ node: React.ReactNode }>;
  value?: string;
}

const Search = ({
  className,
  hasAutoFocus,
  icon,
  isClearable = true,
  isIconVisible = true,
  isScrollable,
  isSearching,
  onChange,
  placeholder = 'Search',
  results,
  value,
}: SearchProps) => {
  const resultsEl = useRef<HTMLUListElement>(null);
  const [currentFocus, setCurrentFocus] = useState(0);

  const hasResults = value && results && results.length > 0;

  useEffect(() => {
    if (hasResults && currentFocus < results.length - 1) {
      Mousetrap.bind(['down'], (e: KeyboardEvent) => {
        e.preventDefault();
        setCurrentFocus(currentFocus + 1);
      });
    }

    if (hasResults && currentFocus > 0) {
      Mousetrap.bind(['up'], (e: KeyboardEvent) => {
        e.preventDefault();
        setCurrentFocus(currentFocus - 1);
      });
    }

    if (hasResults) {
      Mousetrap.bind(['enter'], () => {
        (resultsEl.current?.childNodes[currentFocus].childNodes[0] as HTMLElement).click();
      });
    }

    if (hasResults) {
      Mousetrap.bind(['esc'], () => {
        onChange('');
      });
    }

    if (!hasResults) {
      Mousetrap.reset();

      return () => {
        Mousetrap.reset();
      };
    }
  }, [currentFocus, hasResults, onChange, results]);

  useEffect(() => {
    setCurrentFocus(0);
  }, [value]);

  useEffect(() => {
    return () => {
      Mousetrap.reset();
    };
  }, []);

  const renderIcon = () => {
    if (icon) {
      return cloneElement(icon as React.ReactElement<any>, {
        className: styles['searchbar-search-icon'],
      });
    }
    return <SearchIcon className={styles['searchbar-search-icon']} />;
  };

  return (
    <OutsideClickHandler onOutsideClick={results ? () => onChange('') : () => {}}>
      <div className={classNames(styles['search'], className)}>
        <div
          className={classNames(styles['searchbar'], {
            [styles['is-icon-hidden']]: !isIconVisible,
          })}
          data-testid="searchbar"
        >
          {isIconVisible &&
            (isSearching ? (
              <RepromptIcon className={styles['searchbar-loading-icon']} />
            ) : (
              renderIcon()
            ))}
          <Input
            autoFocus={hasAutoFocus}
            className="mousetrap"
            onChange={onChange}
            placeholder={placeholder}
            value={value}
          />
          {value && isClearable && !isSearching && (
            <button
              aria-label="Clear search"
              className={styles['searchbar-close-icon']}
              onClick={() => onChange('')}
              type="button"
            >
              <CloseBoldIcon />
            </button>
          )}
        </div>
        {hasResults && (
          <ul
            className={classNames(styles['search-results'], {
              [styles['is-scrollable']]: isScrollable,
            })}
            data-testid="search-results"
            ref={resultsEl}
          >
            {results.map((result, index) => (
              <li
                className={index === currentFocus ? styles['is-active'] : ''}
                key={`result-${index}`}
              >
                {result?.node}
              </li>
            ))}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default Search;
